// src/lib/datensicherung.js
// Datensicherung der Datenbank als EINE Datei (Anna-Lena, 18.09.2026).
//
// Supabase Free-Tier macht keine automatischen Backups, und ein pg_dump braucht
// das Datenbank-Passwort. Deshalb liest die App mit der Anmeldung des Admins
// alle Tabellen seitenweise und speichert sie als JSON auf dem PC.
// Nur LESEN - hier wird nichts geaendert.
//
// Nicht enthalten: die Dateien selbst (Fotos, Vertrags-PDFs) im Speicher -
// nur deren Eintraege in den Tabellen.

import { supabase, withTimeout } from './supabase';
import { APP_VERSION } from './version';

// Reihenfolge = Reihenfolge beim Wiederherstellen (erst Stammdaten, dann Protokolle).
export const SICHERUNG_TABELLEN = [
  'user_profiles', 'vnb', 'kunden', 'stationen', 'station_konfiguration',
  'station_ms_felder', 'station_trafos', 'station_wandler',
  'station_erderanlage_schienen', 'station_erderanlage_anschluesse', 'station_vertraege', 'station_schutzpruefungen',
  'modul_vorlagen', 'modul_pruefpunkt_vorlagen',
  'protokolle', 'protokoll_module', 'protokoll_modul_pruefpunkte', 'maengel',
  'protokoll_erderanlage_messungen', 'protokoll_fotos', 'lieferscheine',
  'ueberspannung_sicherungen', 'app_fehler', 'audit_log',
];

const SEITE = 1000;   // PostgREST liefert hoechstens 1000 Zeilen je Anfrage
const LETZTE = 'etb-letzte-datensicherung';

async function tabelleLesen(name) {
  const zeilen = [];
  // Nach id sortiert, damit das seitenweise Lesen nichts doppelt oder gar
  // nicht liefert. Tabellen ohne id (z. B. station_konfiguration) ohne Sortierung.
  let sortieren = true;
  for (let von = 0; ; von += SEITE) {
    let q = supabase.from(name).select('*').range(von, von + SEITE - 1);
    if (sortieren) q = q.order('id');
    let { data, error } = await withTimeout(q, 60000, 'Sicherung ' + name);
    if (error && sortieren && /id/.test(String(error.message || '')) && von === 0) {
      sortieren = false;
      ({ data, error } = await withTimeout(supabase.from(name).select('*').range(von, von + SEITE - 1), 60000, 'Sicherung ' + name));
    }
    if (error) throw error;
    zeilen.push(...(data || []));
    if (!data || data.length < SEITE) break;
  }
  return zeilen;
}

// fortschritt(text, erledigt, gesamt) wird nach jeder Tabelle aufgerufen.
export async function datensicherungErstellen(fortschritt) {
  const ergebnis = {
    art: 'ETB-Wartung Datensicherung',
    erstellt: new Date().toISOString(),
    app_version: APP_VERSION,
    hinweis: 'Nur Tabellen-Inhalte. Fotos und Vertrags-PDFs liegen im Supabase-Speicher und sind hier nicht enthalten.',
    anzahl: {},
    fehler: {},
    tabellen: {},
  };
  let i = 0;
  for (const name of SICHERUNG_TABELLEN) {
    if (fortschritt) fortschritt(name, i, SICHERUNG_TABELLEN.length);
    try {
      const zeilen = await tabelleLesen(name);
      ergebnis.tabellen[name] = zeilen;
      ergebnis.anzahl[name] = zeilen.length;
    } catch (e) {
      // Eine fehlende oder gesperrte Tabelle darf die Sicherung nicht abbrechen -
      // sie steht dann sichtbar unter "fehler".
      ergebnis.fehler[name] = e?.message || String(e);
    }
    i++;
  }
  if (fortschritt) fortschritt('fertig', i, SICHERUNG_TABELLEN.length);
  return ergebnis;
}

export function datensicherungHerunterladen(ergebnis) {
  const d = new Date(ergebnis.erstellt);
  const zwei = (n) => String(n).padStart(2, '0');
  const name = 'ETB-Datensicherung-' + d.getFullYear() + '-' + zwei(d.getMonth() + 1) + '-' + zwei(d.getDate())
    + '_' + zwei(d.getHours()) + zwei(d.getMinutes()) + '.json';
  const blob = new Blob([JSON.stringify(ergebnis)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  try { localStorage.setItem(LETZTE, ergebnis.erstellt); } catch { /* nur Anzeige */ }
  return { name, groesse: blob.size };
}

// --- Monats-Archiv der Protokolle als PDF (Anna-Lena 18.09.2026) --------------
// Nur freigegebene Protokolle, nur das Vollprotokoll, Monat nach Wartungsdatum.
// Die PDFs werden wie beim normalen Download aus den Daten erzeugt und in EINE
// ZIP gepackt. Schlaegt ein Protokoll fehl, steht es in FEHLER.txt - der Rest
// wird trotzdem gesichert.
const dateiTeil = (s) => String(s || '').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 60);

export async function protokollArchivErstellen(jahr, monat, fortschritt) {
  const zwei = (n) => String(n).padStart(2, '0');
  const von = jahr + '-' + zwei(monat) + '-01';
  const bisDatum = new Date(jahr, monat, 1);   // erster Tag des Folgemonats
  const bis = bisDatum.getFullYear() + '-' + zwei(bisDatum.getMonth() + 1) + '-01';
  const { data: liste, error } = await withTimeout(
    supabase.from('protokolle')
      .select('id, wartungs_datum, art, stationen(bezeichnung, stations_nr, kunden(name))')
      .eq('status', 'buero_freigegeben')
      .gte('wartungs_datum', von).lt('wartungs_datum', bis)
      .order('wartungs_datum'),
    60000, 'Protokolle suchen');
  if (error) throw error;
  if (!liste || liste.length === 0) return { anzahl: 0, fehler: [] };

  const [{ default: JSZip }, { vollprotokollAlsBlob }] = await Promise.all([import('jszip'), import('./pdfExport')]);
  const zip = new JSZip();
  const fehler = [];
  const vergeben = new Set();
  for (let i = 0; i < liste.length; i++) {
    const p = liste[i];
    const st = p.stationen || {};
    if (fortschritt) fortschritt(i, liste.length, st.kunden?.name || st.bezeichnung || '');
    let name = [String(p.wartungs_datum || '').slice(0, 10), dateiTeil(st.kunden?.name), dateiTeil(st.bezeichnung || st.stations_nr),
      p.art === 'sichtpruefung' ? 'Sichtpruefungsprotokoll' : 'Wartungsprotokoll'].filter(Boolean).join(' ');
    // Zwei Protokolle am selben Tag an derselben Station: Nummer anhaengen.
    let k = 2; const basis = name;
    while (vergeben.has(name)) name = basis + ' (' + (k++) + ')';
    vergeben.add(name);
    try {
      const { blob } = await vollprotokollAlsBlob(p.id);
      zip.file(name + '.pdf', await blob.arrayBuffer());
    } catch (e) {
      fehler.push(name + ': ' + (e?.message || e));
    }
  }
  if (fehler.length) zip.file('FEHLER.txt', 'Diese Protokolle konnten nicht erzeugt werden:\r\n\r\n' + fehler.join('\r\n'));
  if (fortschritt) fortschritt(liste.length, liste.length, 'ZIP wird gepackt');
  // PDFs sind schon komprimiert - STORE ist schneller und kaum groesser.
  const inhalt = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
  const dateiname = 'ETB-Protokolle-' + jahr + '-' + zwei(monat) + '.zip';
  const url = URL.createObjectURL(inhalt);
  const a = document.createElement('a');
  a.href = url; a.download = dateiname;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return { anzahl: liste.length - fehler.length, fehler, dateiname, groesse: inhalt.size };
}

export function letzteDatensicherung() {
  try { return localStorage.getItem(LETZTE); } catch { return null; }
}
